// How much of the day it is, for the ambience effects that only belong at one end of it: windblown
// leaves by day (wind_leaves.ts), fog by night (night_fog.ts). One place, so the two cross-fade
// against the same numbers instead of drifting apart.
//
// WHY BRIGHTNESS AND NOT THE PHASE NAME. The engine has six phases, each with a brightness
// (game-common/mechanics/physics_mechanics.ts):
//
//     darkestNight 0.2   night 0.3   dawn 0.4   dusk 0.5   morning 0.7   day 1.0
//
// and the client walks `currentBrightness` between them a hundredth at a time rather than snapping
// (ClientGameLogic.updateDayPhase). Reading the interpolated value means an effect dies across the
// whole of dusk and gets up across the whole of the morning; reading the phase NAME would step it,
// and a wind that stops mid-gust on a phase boundary looks like a bug.

// At or below STILL there is no daylight left as far as these effects are concerned; at or above FULL
// it is broad day; between them it ramps. This puts dawn (0.4) and every darker phase at zero and
// morning (0.7) upward at one, leaving dusk (0.5) as the half-lit third.
//
// 0.42 sits deliberately BETWEEN two phase brightnesses. On a threshold a settled phase could land
// exactly on, an effect that starts and stops at the boundary would flicker on and off forever.
export const STILL_BRIGHTNESS = 0.42
export const FULL_BRIGHTNESS = 0.7

/**
 * 1 in broad daylight, 0 once it is properly dark, ramping between.
 *
 * `undefined` means the room has no day cycle at all -- ClientGameLogic leaves `dayPhase` null for a
 * cave, a house or a generated blank room, and `currentBrightness` sits at 0.0 there. That counts as
 * permanent DAY, not as permanent night: such a room has fixed lighting and no hour to follow, and
 * passing its 0.0 through would becalm it (and fill it with fog) for reasons that have nothing to do
 * with the time of day.
 */
export const daylightFraction = (brightness?: number): number => {
    if (brightness === undefined) {
        return 1
    }
    const span = FULL_BRIGHTNESS - STILL_BRIGHTNESS
    return Math.min(1, Math.max(0, (brightness - STILL_BRIGHTNESS) / span))
}

// The complement, for the effects that belong to the dark. Fog rising as the leaves settle is the
// same ramp read backwards, which is why the two hand over cleanly at dusk.
export const nightFraction = (brightness?: number): number => 1 - daylightFraction(brightness)
